import { useState } from 'react'
import { Button, ModalButton, TinyMCE } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useRecoilValue, useRecoilCallback, useSetRecoilState } from 'recoil'
import {
  itemAtomFamily,
  itemIdAtom,
  stylizedVersesAtom,
  themeOverrideAtom,
  editorProductIdAtom,
  unsavedChangesAtom,
} from 'atoms'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import { useEditorInsertsLotus } from '@/hooks/editor/useEditorInsertsLotus'
import { useEditorInsertHtmlTextBox } from '@/hooks/editor/useEditorInsertHtmlTextBox'
import { v4 as uuidv4 } from 'uuid'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import zLayers from 'utils/styling/zLayers'
import { PersonalImageCard } from 'components/dashboard/CaseDetails'
import { getImageDimensions } from 'utils'
import { useTranslation } from 'react-i18next'
import { deleteCustomVerse, getCustomVerses } from '@/api/customVerses'
import { getThemeOverrideProductId } from 'utils/editor/themeOverrideProductId'

export const InsertCustomVerse = ({ verse, setEditing, setShow }) => {
  const { t } = useTranslation()
  const handleError = useErrorHandler('InsertCustomVerse')
  const setStylizedVerses = useSetRecoilState(stylizedVersesAtom)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const itemIds = useRecoilValue(itemIdAtom)

  const themeOverride = useRecoilValue(themeOverrideAtom)
  const productId = useRecoilValue(editorProductIdAtom)
  const isAdmin = useIsAdmin()
  const verseInsertsLotus = useEditorInsertsLotus()
  const insertHtmlTextBox = useEditorInsertHtmlTextBox()

  const isStylizedVerse = !verse.html && verse.type === 'IMAGE'
  const isGlobalVerse = verse.is_global
  const showActionsForGlobalVerse = isGlobalVerse && isAdmin
  const shouldShowActions =
    !isStylizedVerse && (!isGlobalVerse || showActionsForGlobalVerse)

  const { theme_id: defaultThemeId } = themeOverride || {}
  const defaultProductId = getThemeOverrideProductId(themeOverride)

  const deleteVerse = async (verse) => {
    try {
      await deleteCustomVerse(verse.id)

      const stylizedVersesData = await getCustomVerses(
        defaultThemeId,
        defaultProductId,
        productId,
      )
      setStylizedVerses(stylizedVersesData.data)

      setConfirmOpen(false)
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  const addToItems = useRecoilCallback(({ set }) => (item) => {
    set(itemAtomFamily(item.id), item)
    set(itemIdAtom, [...itemIds, item.id])
    set(unsavedChangesAtom, true)
  })

  const insertVerseHTML = async () => {
    const uuid = uuidv4()

    const textObj = {
      id: uuid,
      type: 'text',
      x: 0,
      y: 0,
      z: zLayers.content,
      width: 250,
      height: 250,

      text: {
        value: verse.html,
        edits: {},
      },
    }
    if (verseInsertsLotus) {
      // A verse the converter refuses goes in as this TinyMCE item.
      try {
        if ((await insertHtmlTextBox(textObj)) !== null) setShow(false)
      } catch (error) {
        handleError(error?.message, error)
      }
      return
    }
    addToItems(textObj)
    setShow(false)
  }

  const insertVerseImage = async () => {
    const uuid = uuidv4()
    const d = await getImageDimensions(verse.image_url)
    const height = 150
    const width = height * (d.width / d.height)
    const imageObj = {
      id: uuid,
      type: 'image',
      x: 0,
      y: 0,
      z: zLayers.content,
      height: height,
      width: width,
      assetLocked: false,
      properties: {
        subtype: 'stylizedVerse',
        caseImage: '',
      },
      image: {
        url: verse.image_url,
        edits: {},
      },
    }
    addToItems(imageObj)
    setShow(false)
  }

  return (
    <Flex full column>
      <Flex
        full
        margin="0 0 12px 0"
        clickable
        data-testid="custom-verse-insert"
        onClick={() =>
          isStylizedVerse ? insertVerseImage() : insertVerseHTML()
        }
      >
        {!isStylizedVerse && (
          <Flex
            full
            clickThrough
            padding="1px"
            background={isStylizedVerse ? undefined : 'lockedWhite'}
          >
            <TinyMCE init={{ toolbar: [] }} value={verse.html} />
          </Flex>
        )}
        {isStylizedVerse && (
          <Flex full column width="100%">
            <PersonalImageCard
              image={verse.image_url}
              index={0}
              makeDefault={true}
              margin="0 0 24px 0"
              allowDelete={false}
              backgroundColor="#EDEFF0"
            />
          </Flex>
        )}
      </Flex>
      {shouldShowActions ? (
        <Flex>
          <ModalButton
            text={t('editor.menus.assets.stylizedVerses.deleteButton')}
            margin="0 12px 0 0"
            show={[confirmOpen, setConfirmOpen]}
          >
            <Flex margin="24px">
              <Flex column>
                <Text weight="600" size="16px" margin="0 0 24px 0">
                  {t('editor.menus.assets.stylizedVerses.areYouSure')}
                </Text>
                <Flex>
                  <Button
                    margin="0 12px 0 0"
                    onClick={() => setConfirmOpen(false)}
                  >
                    {t('editor.menus.assets.stylizedVerses.cancelButton')}
                  </Button>
                  <Button destructive onClick={() => deleteVerse(verse)}>
                    {t('editor.menus.assets.stylizedVerses.confirmButton')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </ModalButton>
          <Button primary onClick={() => setEditing(true)} margin="0 12px 0 0">
            {t('editor.menus.assets.stylizedVerses.editButton')}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  )
}

export default InsertCustomVerse
